import { useEffect, useMemo, useState } from 'react';
import { DeepPartial, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { array, InferType, object } from 'yup';

import {
  PriceReadModel,
  SetAgreementProductPayload,
  TenantErpIntegrationReadModel,
  useGetAgreementsByIdQuery,
  useGetPricesQuery,
  usePutAgreementsByAgreementIdProductsMutation,
  PermissionKeys,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  FormSubmitButton,
  isDefinedString,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  useFormAdd,
} from '@client/toolkit';
import { yupResolver } from '@hookform/resolvers/yup';

import { AgreementCommonCostItem } from './agreement-common-cost-item.component';
import {
  AgreementCommonCostFormData,
  AgreementCommonCostItemForm,
  agreementCommonCostSchema,
  ChildOverrideState,
} from './agreement-common-cost-item.form';

import { CancelRequestModal, ProductPricesAddModal } from '../../../../../components';
import { AgreementPermissionBasedActionButton } from '../../../../../components/agreement-permission-based-button/agreement-permission-based-button';
import { useAgreementPermissions } from '../../../../../hooks/useAgreementPermissions/useAgreementPermissions';
import { useTenants } from '../../../../../hooks';

const schema = object({
  products: array().of(agreementCommonCostSchema).required(),
});

type FormValues = InferType<typeof schema>;
type FormDefaultValues = DeepPartial<FormValues>;

export interface AgreementCommonCostsCardProps {
  className?: string;
}

export function AgreementCommonCostsCard({ className }: AgreementCommonCostsCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const { t: tAgreements } = useTranslation('agreements');

  const { id } = useParams<{ id: string }>();
  const { selectedTenant } = useTenants();

  // Check if ERP is configured for the current tenant
  const isErpConfigured = selectedTenant && typeof selectedTenant === 'object' && 'erpIntegrations' in selectedTenant
    ? !!selectedTenant.erpIntegrations?.some((erp: TenantErpIntegrationReadModel) => erp.configured)
    : false;

  const [editing, setEditing] = useState<boolean>(false);

  // Track which product accordions are expanded
  // Two sets: one for persisted products (by agreement product ID, persists across modes)
  // and one for new unsaved products (by field ID, edit mode only)
  const [expandedProductIds, setExpandedProductIds] = useState<Set<string>>(new Set());
  const [expandedFieldIds, setExpandedFieldIds] = useState<Set<string>>(new Set());

  const toggleExpandedById = (id: string) => {
    setExpandedProductIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleExpandedByFieldId = (fieldId: string) => {
    setExpandedFieldIds((prev) => {
      const next = new Set(prev);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      return next;
    });
  };

  const { data: agreement, isFetching, isLoading } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });
  const { hasPermission } = useAgreementPermissions({ id });
  const canEdit = hasPermission([PermissionKeys.Agreement.Update]);

  const isAgreementActive = agreement?.status === 'Effective';
  const isTerminated = agreement?.status === 'Terminated';

  const { data: pricesData, isFetching: isFetchingPrices } = useGetPricesQuery({ 'pagination.Limit': 250 }, { skip: !canEdit });
  const allPrices = useMemo(() => pricesData?.items ?? [], [pricesData?.items]);

  const [putAgreementProducts] = usePutAgreementsByAgreementIdProductsMutation();

  // Track children overrides per product field index
  const [childrenByIndex, setChildrenByIndex] = useState<Record<number, ChildOverrideState[]>>({});
  const [childrenDirty, setChildrenDirty] = useState(false);

  const handleChildrenChange = (fieldIndex: number, children: ChildOverrideState[]) => {
    setChildrenByIndex((prev) => ({ ...prev, [fieldIndex]: children }));
  };

  const handleChildOverrideDirty = () => {
    setChildrenDirty(true);
  };

  // Calculate total area from agreement spaces (using override area if set)
  const totalArea = useMemo(() => {
    if (!agreement?.spaces || agreement.spaces.length === 0) return 0;
    return agreement.spaces.reduce((sum, space) => {
      const area = space.overrideArea ?? space.space?.area?.metric ?? 0;
      return sum + area;
    }, 0);
  }, [agreement?.spaces]);

  // Filter products to only show Felleskostnader (by Category or AreaMetric unit scheme)
  const isFelleskost = (x: { price?: { product?: { category?: string; unit?: { scheme?: string } | null } | null } | null }) =>
    x.price?.product?.category === 'Felleskostnader' ||
    x.price?.product?.unit?.scheme === 'AreaMetric';

  const agreementCommonCosts = useMemo(() => {
    return (
      agreement?.products?.filter((x) =>
        isFelleskost(x) && !x.parentAgreementProductId
      ) ?? []
    );
  }, [agreement?.products]);

  // Other products that are NOT Felleskostnader (to preserve when saving)
  const otherAgreementProducts = useMemo(() => {
    return agreement?.products?.filter((x) => 
      !isFelleskost(x) && !x.parentAgreementProductId
    ) ?? [];
  }, [agreement?.products]);

  const onSubmit = async (data: FormValues) => {
    if (id == null) return;

    // Helper to clean overwrittenUnitAmount - return null if empty
    const cleanOverwrittenUnitAmount = (amount: any) => {
      if (!amount) return null;
      if (amount.amount == null || amount.amount === '') return null;
      return amount;
    };

    // Preserve other (non-area-based) products
    const existingPayload: SetAgreementProductPayload[] =
      otherAgreementProducts.map<SetAgreementProductPayload>(
        (x): SetAgreementProductPayload => ({
          priceId: x.price?.id ?? '',
          quantity: x.quantity as number,
          id: x.id,
          interval: x.interval ?? null,
          overwrittenUnitAmount: cleanOverwrittenUnitAmount(x.overwrittenUnitAmount),
          overrideArea: x.overrideArea ?? null,
        }),
      ) ?? [];

    // Clean the form data products - always save quantity=1, use overrideArea for area override
    const cleanedProducts = data.products.map((p, index) => {
      // Build children payload from tracked overrides
      const childOverrides = childrenByIndex[index];
      const children = childOverrides?.map((c) => ({
        id: c.agreementChildId ?? undefined,
        priceId: c.priceId,
        overwrittenUnitAmount: c.overwrittenUnitAmount ?? undefined,
      })) ?? undefined;

      return {
        id: p.id,
        priceId: p.priceId,
        quantity: 1, // Always 1 - backend multiplies by effective area
        overrideArea: p.overrideArea ?? null,
        interval: null, // Common costs don't have intervals
        overwrittenUnitAmount: cleanOverwrittenUnitAmount(p.overwrittenUnitAmount),
        children: children && children.length > 0 ? children : undefined,
      };
    });

    try {
      await putAgreementProducts({
        agreementId: id,
        body: [...existingPayload, ...cleanedProducts],
      }).unwrap();
    } catch (e) {
      return;
    }

    setExpandedProductIds(new Set());
    setExpandedFieldIds(new Set());
    setEditing(false);
  };

  const defaultValues: FormDefaultValues = useMemo(
    () => ({
      products: agreementCommonCosts.map<AgreementCommonCostFormData>((data) => ({
        id: data.id ?? undefined,
        priceId: data.price?.id ?? '',
        quantity: 1, // Always 1 - backend multiplies by effective area
        overrideArea: data.overrideArea ?? null,
        overwrittenUnitAmount: data.overwrittenUnitAmount ?? null,
      })),
    }),
    [agreementCommonCosts, totalArea],
  );

  const methods = useForm<FormValues>({
    defaultValues,
    mode: 'onTouched',
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    formState: { isDirty },
    reset,
    control,
  } = methods;

  const {
    fields: editedCommonCosts,
    items: productPrices,
    setItems: setProductPrices,
    adding: addingProductPrices,
    enableAdding: handleOpenAddProductPrices,
    disableAdding: handleCloseAddProductPrices,
    appendNewFormItems,
    removeItemByFieldIndex: handleRemoveProductPrice,
    getItemByFieldIndex: getProductPriceByFieldIndex,
  } = useFormAdd<AgreementCommonCostFormData, PriceReadModel>({
    control,
    name: 'products',
    mapItemToFormItem: (x): AgreementCommonCostFormData => ({ 
      id: undefined, 
      priceId: x.id, 
      quantity: 1, // Always 1 - backend multiplies by effective area
      overrideArea: null, // No override by default - use calculated area from spaces
      overwrittenUnitAmount: null 
    }),
    itemEqualsFormItem: (a, x) => a.id === x.priceId,
    formItemEqualsFormItem: () => false,
  });

  // Custom add handler: only append NEW items so existing fields (and their
  // local child-override state) are preserved — setNewItems would remove()/
  // append() ALL fields, causing every form item to remount and lose state.
  const handleAddProductPrices = (newPrices: PriceReadModel[]) => {
    const existingCount = productPrices.length;
    const addedPrices = newPrices.slice(existingCount);
    if (addedPrices.length > 0) {
      appendNewFormItems(
        addedPrices.map((x) => ({
          id: undefined,
          priceId: x.id,
          quantity: 1,
          overrideArea: null,
          overwrittenUnitAmount: null,
        })),
      );
    }
    setProductPrices(newPrices);
    handleCloseAddProductPrices();
  };

  useEffect(() => {
    setProductPrices(agreementCommonCosts.map((x) => x.price).filter((x): x is PriceReadModel => x != null));
    reset(defaultValues);
    setChildrenByIndex({});
    setChildrenDirty(false);
    setExpandedFieldIds(new Set());
  }, [defaultValues, editing]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasCommonCosts = agreementCommonCosts != null && agreementCommonCosts.length <= 0;
  const hasEditedCommonCosts = editedCommonCosts != null && editedCommonCosts.length <= 0;

  const isListEmpty = useMemo(
    () => (!editing ? hasCommonCosts : hasEditedCommonCosts),
    [editing, hasCommonCosts, hasEditedCommonCosts],
  );

  const handleOpenEdit = () => {
    setEditing(true);
  };

  const [cancelingRequest, setCancelingRequest] = useState<boolean>(false);

  const checkCancelRequest = () => {
    if (isDirty) {
      setCancelingRequest(true);
    } else {
      setExpandedProductIds(new Set());
      setExpandedFieldIds(new Set());
      setEditing(false);
    }
  };

  const handleConfirmCancelRequest = () => {
    setExpandedProductIds(new Set());
    setExpandedFieldIds(new Set());
    setEditing(false);
    setCancelingRequest(false);
  };

  const handleCancelCancelRequest = () => {
    setCancelingRequest(false);
  };

  // Calculate total amount for footer - separate yearly and one-time
  const totalAmount = useMemo(() => {
    if (!agreementCommonCosts || agreementCommonCosts.length === 0) return undefined;
    
    let yearlyTotal = 0;
    let oneTimeTotal = 0;
    
    for (const product of agreementCommonCosts) {
      const effectiveUnitAmount = product.overwrittenUnitAmount ?? product.price?.unitAmount;
      // Effective area: use overrideArea if set, otherwise use totalArea from spaces
      const effectiveArea = product.overrideArea ?? totalArea ?? 0;
      if (!effectiveUnitAmount || effectiveArea <= 0) continue;
      
      let productTotal = effectiveUnitAmount.amount * effectiveArea;
      
      if (product.price?.recurring) {
        // For monthly recurring products, multiply by 12 to get yearly amount
        if (product.price?.interval?.type === 'Month') {
          productTotal = productTotal * 12;
        }
        yearlyTotal += productTotal;
      } else {
        oneTimeTotal += productTotal;
      }
    }
    
    const formatNumber = (num: number) => new Intl.NumberFormat('nb-NO', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
    
    const parts: string[] = [];
    if (oneTimeTotal !== 0) {
      parts.push(`${formatNumber(oneTimeTotal)} kr Engangs`);
    }
    if (yearlyTotal !== 0) {
      parts.push(`${formatNumber(yearlyTotal)} kr / år`);
    }
    
    return parts.length > 0 ? parts.join(' + ') : undefined;
  }, [agreementCommonCosts, totalArea]);

  return (
    <>
      <Card className={className}>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <CardHeader>
              <CardTitle className="flex-1" variant="highlighted">
                {tGlobal('entity.agreementCommonCost', { context: 'plural' })}
              </CardTitle>
              <CardActions>
                {!editing ? (
                  <AgreementPermissionBasedActionButton
                    agreementId={id ?? ''}
                    permissions={[PermissionKeys.Agreement.Update]}
                    onClick={handleOpenEdit}
                    disabled={isFetching || id == null || agreement == null || isTerminated}
                    disabledTooltip={isTerminated ? tAgreements('terminatedAgreementEditDisabled') : undefined}
                    title={tCommon('edit')}
                  />
                ) : (
                  <>
                    <Button color="primary-dark" size="sm" onClick={checkCancelRequest}>
                      {tCommon('cancel')}
                    </Button>
                    <Button size="sm" color="primary-dark" disabled={isFetching} onClick={handleOpenAddProductPrices}>
                      {tCommon('add')}
                    </Button>
                    <FormSubmitButton type="submit" color="primary" size="sm" disabled={isFetching} disableDirtyHandling={childrenDirty}>
                      {tCommon('save')}
                    </FormSubmitButton>
                  </>
                )}
              </CardActions>
            </CardHeader>

            <CardContent variant="fill" className="justify-self-center rounded-lg w-full px-4 pb-4">
              <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
                {!isLoading && isListEmpty && (
                  <caption className="px-4 py-4 [caption-side:bottom]">
                    <EmptyPlaceholder>
                      {tCommon('noEntitySpecified', {
                        entity: tGlobal('entity.agreementCommonCost', { context: 'plural' }).toLowerCase(),
                      })}
                    </EmptyPlaceholder>
                  </caption>
                )}

                <TableHead>
                  <TableHeadRow>
                    <TableHeadCell className="w-[20%]">
                      {tGlobal('agreementCommonCost.productName')}
                    </TableHeadCell>
                    <TableHeadCell className="w-[15%]">{tGlobal('agreementCommonCost.area')}</TableHeadCell>
                    <TableHeadCell className="w-[15%]">{tGlobal('agreementCommonCost.overrideArea')}</TableHeadCell>
                    <TableHeadCell className="w-[18%]">{tGlobal('agreementCommonCost.productPrice')}</TableHeadCell>
                    <TableHeadCell className="w-[15%]">{tGlobal('agreementCommonCost.overwrittenPrice')}</TableHeadCell>
                    <TableHeadCell className="w-[8%]">{tGlobal('agreementCommonCost.isInvoiced')}</TableHeadCell>
                    <TableHeadCell className="w-[10%]" align="right">{tGlobal('agreementCommonCost.totalAmount')}</TableHeadCell>
                    {editing && <TableHeadCell className="w-[2%]" />}
                  </TableHeadRow>
                </TableHead>

                <TableBody>
                  {isLoading &&
                    Array.from(Array(2).keys()).map((x) => <AgreementCommonCostItem key={x} isFetching />)}

                  {!isLoading &&
                    !editing &&
                    agreementCommonCosts?.map((product) => (
                      <AgreementCommonCostItem 
                        key={product.id} 
                        agreementProduct={product} 
                        totalArea={totalArea}
                        isFetching={isFetching}
                        expanded={expandedProductIds.has(product.id ?? '')}
                        onToggleExpanded={() => product.id && toggleExpandedById(product.id)}
                        canEdit={canEdit}
                      />
                    ))}

                  {!isLoading &&
                    editing &&
                    editedCommonCosts?.map((field, index) => {
                      const currentProduct = agreementCommonCosts.find(p => p.id === methods.getValues(`products.${index}.id`));
                      const isReadOnly = isErpConfigured && currentProduct?.isInvoiced;
                      const canRemove = !isReadOnly;
                      
                      return (
                        <AgreementCommonCostItemForm
                          key={field.id}
                          fieldIndex={index}
                          productPrice={getProductPriceByFieldIndex(index)}
                          totalArea={totalArea}
                          isFetching={isFetching || isFetchingPrices}
                          isInvoiced={currentProduct?.isInvoiced}
                          readOnly={isReadOnly}
                          onRemove={canRemove ? handleRemoveProductPrice : undefined}
                          persistedChildren={
                            agreementCommonCosts.find(
                              (ac) => ac.id === methods.getValues(`products.${index}.id`),
                            )?.children
                          }
                          onChildrenChange={handleChildrenChange}
                          onChildOverrideDirty={handleChildOverrideDirty}
                          expanded={(() => {
                            const pid = methods.getValues(`products.${index}.id`);
                            return pid ? expandedProductIds.has(pid) : expandedFieldIds.has(field.id);
                          })()}
                          onToggleExpanded={() => {
                            const pid = methods.getValues(`products.${index}.id`);
                            if (pid) toggleExpandedById(pid);
                            else toggleExpandedByFieldId(field.id);
                          }}
                        />
                      );
                    })}
                </TableBody>

                {!isLoading && !editing && !isListEmpty && (
                  <tfoot>
                    <tr className="border-double border-t-2 border-t-border">
                      <th align="left" colSpan={7} className="text-sm px-4 pt-2 pb-1">
                        <div className="flex items-center">
                          <div className="flex-1 font-medium text-white">{tCommon('total')}</div>
                          <Text
                            className="text-sm font-medium whitespace-nowrap text-white"
                            isEmpty={!isDefinedString(totalAmount)}
                          >
                            {totalAmount}
                          </Text>
                        </div>
                      </th>
                    </tr>
                  </tfoot>
                )}
              </Table>
            </CardContent>
          </form>
        </FormProvider>
      </Card>

      <CancelRequestModal
        open={cancelingRequest}
        onConfirm={handleConfirmCancelRequest}
        onCancel={handleCancelCancelRequest}
      />

      {editing && (
        <ProductPricesAddModal
          title={tGlobal('entity.agreementCommonCost', { context: 'plural' })}
          open={addingProductPrices}
          selected={productPrices}
          onClose={handleCloseAddProductPrices}
          onSubmit={handleAddProductPrices}
          unitSchemes={['AreaMetric']}
        />
      )}
    </>
  );
}
